// SPEC-0022 freshness: whether a chip's load % can be trusted.
//
// Only the edited activities are stale (PDR-0103): every other chip keeps the
// colour of the last *Update Usages* run. Two sources:
// - the row's own `loadStale`, from the backend (its inputs changed after the
//   last run, possibly in another session);
// - a local edit, known before any re-read: only the rows the write names.
//
// Contract at the end of an *Update Usages* job (ALL_COMPLETED frame):
// - `loadBadges` = { scenarioId, usagesComputedAt, changed, truncated }, null
//   when the load option is off;
// - `loadBadgesFailed: true` when the badge step failed: nothing is applied,
//   every chip stays as stale as it was.
// The backend stamps usages at job START, so an edit made during the run stays
// stale: local edits are kept when they are newer than the job start.
//
// A plain module store, read by the chip renderers at render time (they run
// outside React), scoped to the scenario of the Gantt that is open
// (useUsagesFreshness). Leaf module: no Gantt/config imports.

export const LOAD_BADGES_UPDATED_EVENT = "offolio:load-badges-updated";

let scenarioId = null;
// id -> { seq, record }: seq orders edits against job starts.
const locallyStale = new Map();
let seq = 0;

const key = (id) => String(id);

// Change notification for React readers (the load popover), outside the
// render path of the chips.
const listeners = new Set();
let version = 0;
const changed = () => {
  version += 1;
  listeners.forEach((l) => l());
};
export const onUsagesFreshnessChange = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getUsagesFreshnessVersion = () => version;

export const setFreshnessScenario = (id) => {
  scenarioId = id ?? null;
};

export const resetUsagesFreshness = () => {
  scenarioId = null;
  locallyStale.clear();
  changed();
};

// Takes the record (its backend `loadStale`) or a bare id (local edits only).
export const isActivityUsagesStale = (recordOrId) => {
  if (recordOrId == null) return false;
  if (typeof recordOrId === "object") {
    const backendStale = recordOrId.loadStale !== undefined ? recordOrId.loadStale : recordOrId.get?.("loadStale");
    return backendStale === true || (recordOrId.id != null && locallyStale.has(key(recordOrId.id)));
  }
  return locallyStale.has(key(recordOrId));
};

// Bryntum repaints the row of a record whose data changes, so setting the
// flag repaints that row only.
export const markActivityStale = (record) => {
  if (record?.id == null) return;
  seq += 1;
  locallyStale.set(key(record.id), { seq, record });
  record.set?.("loadStale", true);
  changed();
};

// Taken when an *Update Usages* job is dispatched; edits after it survive the
// job's push.
export const markUsagesJobStart = () => {
  seq += 1;
  return seq;
};

const writeRecord = (record, field, value) => {
  if (!record || record.isDestroyed) return;
  if (record.set) record.set(field, value);
  else record[field] = value;
};

// Writes the pushed badges onto the loaded rows. Only the `changed` couples
// move; the other resources of a row keep their badge. A row still edited
// locally keeps its grey. Records are written, nothing else: an inline field
// open on a chip stays open (`gantt` is accepted for that contract only).
// eslint-disable-next-line no-unused-vars
export const applyPushToRecords = (taskStore, push, gantt) => {
  if (!taskStore) return;
  const changed = Array.isArray(push?.changed) ? push.changed : [];

  const byActivity = new Map();
  changed.forEach(({ scheduleActivityId, resourceId, peakLoadPct }) => {
    if (scheduleActivityId == null || resourceId == null) return;
    const id = key(scheduleActivityId);
    if (!byActivity.has(id)) byActivity.set(id, { activityId: scheduleActivityId, badges: {} });
    byActivity.get(id).badges[resourceId] = peakLoadPct ?? null;
  });

  taskStore.beginBatch?.();
  try {
    byActivity.forEach(({ activityId, badges }, id) => {
      const record = taskStore.getById(activityId) ?? taskStore.getById(id);
      if (!record) return;
      writeRecord(record, "loadBadges", { ...(record.loadBadges || {}), ...badges });
      writeRecord(record, "loadStale", locallyStale.has(id));
    });
    // Rows the backend reported stale were recomputed by this run too, even
    // when their peak did not move (absent from `changed`). One pass per job.
    taskStore
      .query?.((record) => record.loadStale === true && !locallyStale.has(key(record.id)))
      ?.forEach((record) => writeRecord(record, "loadStale", false));
  } finally {
    taskStore.endBatch?.();
  }
};

// Called at the end of an *Update Usages* job with the frame's `loadBadges`.
// Ignored when null, or for a scenario other than the open Gantt's.
export const applyLoadBadgePush = (push, { jobStart = Infinity } = {}) => {
  if (!push || scenarioId == null || key(push.scenarioId) !== key(scenarioId)) return;
  locallyStale.forEach((entry, id) => {
    if (entry.seq > jobStart) return;
    locallyStale.delete(id);
    // Reset so the next edit's set("loadStale", true) is a real change.
    writeRecord(entry.record, "loadStale", false);
  });
  window.dispatchEvent(new CustomEvent(LOAD_BADGES_UPDATED_EVENT, { detail: push }));
  changed();
};
